'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { BlogCategoryDocument } from '../../../prismicio-types';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface PostData {
  uid: string;
  id: string;
  title: string;
  description: string;
  image: string | null;
  imageAlt: string;
  date: string | null;
  categoryUid: string | null;
  categoryName: string | null;
  author: { name: string; avatar: string | null; uid: string } | null;
  readDuration: number | null;
  featured: boolean;
  type: 'insights' | 'ebooks' | 'webinars';
  href: string | null;
}

interface ApiResponse {
  posts: PostData[];
  page: number;
  pageSize: number;
  hasMore: boolean;
}

type SortOption = 'relevant' | 'oldest' | 'newest';

const CONTENT_TYPES = [
  { id: 'insights', label: 'Insights' },
  { id: 'ebooks', label: 'Ebooks' },
  { id: 'webinars', label: 'Webinars' },
] as const;

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'relevant', label: 'More relevant' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'newest', label: 'Newest' },
];

const PAGE_SIZE = 9;
const MIN_SEARCH_LENGTH = 4;

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function CheckBox({
  checked,
  onChange,
  rounded = false,
}: {
  checked: boolean;
  onChange: () => void;
  rounded?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`relative flex size-6 shrink-0 items-center justify-center border transition-colors ${
        rounded ? 'rounded-full' : 'rounded-[5px]'
      } ${
        checked ? 'border-[#4b0fea] bg-[#4b0fea]' : 'border-[#d4d4d4] bg-white'
      }`}
      aria-checked={checked}
      role="checkbox"
    >
      {checked && (
        <svg
          width="14"
          height="10"
          viewBox="0 0 14 10"
          fill="none"
          className="text-white"
        >
          <path
            d="M1 5L5 9L13 1"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );
}

function RadioButton({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`relative flex size-6 shrink-0 items-center justify-center rounded-full border transition-colors ${
        checked ? 'border-[#4b0fea]' : 'border-[#aaa]'
      }`}
      aria-checked={checked}
      role="radio"
    >
      {checked && <div className="size-3 rounded-full bg-[#4b0fea]" />}
    </button>
  );
}

function SearchIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-black/60"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function getTypeBadge(type: PostData['type']): {
  label: string;
  style: string;
} {
  switch (type) {
    case 'ebooks':
      return { label: 'EBOOK', style: '' };
    case 'webinars':
      return { label: 'WEBINARS', style: '' };
    case 'insights':
    default:
      return { label: 'INSIGHTS', style: '' };
  }
}

function getCtaLabel(type: PostData['type']): string {
  switch (type) {
    case 'ebooks':
      return 'Get Ebook';
    case 'webinars':
      return 'Access webinar';
    case 'insights':
    default:
      return 'Read more';
  }
}

function getPostHref(post: PostData): string {
  // Ebooks point at the landing page set on the document
  // (link_to_landing_page); the type's own /ebooks/:uid route is only a
  // fallback for older entries that haven't been migrated.
  if (post.type === 'ebooks') {
    return post.href || `/ebooks/${post.uid}`;
  }
  if (post.type === 'webinars') {
    return post.href || `/resources-center/webinar/${post.uid}`;
  }
  return post.href || `/resources-center/${post.uid}`;
}

function ResourceCard({ post }: { post: PostData }) {
  return (
    <Link
      href={getPostHref(post)}
      className="group flex flex-col overflow-hidden rounded-2xl bg-[#f8fafb] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] transition-shadow hover:shadow-md"
    >
      {/* Image */}
      <div className="relative aspect-video w-full overflow-hidden rounded-t-2xl">
        {post.image ? (
          <Image
            src={post.image}
            alt={post.imageAlt || post.title}
            fill
            className="absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-105"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        ) : (
          <div className="flex size-full items-center justify-center bg-gray-200">
            <span className="text-sm text-gray-400">No image</span>
          </div>
        )}

        {/* Type badge on image */}
        <span
          className="absolute left-4 top-3 inline-flex items-center rounded-[20px] border border-white/70 px-2 py-0.5 text-xs font-bold uppercase text-white backdrop-blur-[21px]"
          style={{
            backgroundImage:
              'linear-gradient(138deg, rgba(255,255,255,0.47) 0%, rgba(255,255,255,0) 111%)',
          }}
        >
          {getTypeBadge(post.type).label}
        </span>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-2 p-4">
        {/* Category with green left border */}
        {post.categoryName && (
          <div className="flex items-center border-l border-[#00f557] py-1 pl-2">
            <span className="text-xs font-semibold uppercase text-[#041118]">
              {post.categoryName}
            </span>
          </div>
        )}

        {/* Title */}
        <h3 className="line-clamp-3 text-base sm:text-xl font-semibold leading-[1.4] text-[#21007c]">
          {post.title}
        </h3>

        {/* Description + CTA */}
        <div className="flex flex-1 flex-col gap-6">
          <p className="line-clamp-3 flex-1 text-sm font-bold leading-[1.4] text-[#002726]">
            {post.description}
          </p>

          <span className="inline-flex self-start border-b border-[#4b0fea] text-sm font-semibold text-[#041118]">
            {getCtaLabel(post.type)}
          </span>
        </div>
      </div>
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/*  Filter sidebar (mobile: collapsible)                               */
/* ------------------------------------------------------------------ */

function FilterSidebar({
  categories,
  activeTypes,
  activeCategories,
  onToggleType,
  onToggleCategory,
  onClearTypes,
  onClearCategories,
  onClearAll,
}: {
  categories: BlogCategoryDocument[];
  activeTypes: Set<string>;
  activeCategories: Set<string>;
  onToggleType: (type: string) => void;
  onToggleCategory: (uid: string) => void;
  onClearTypes: () => void;
  onClearCategories: () => void;
  onClearAll: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const hasFilters = activeTypes.size > 0 || activeCategories.size > 0;

  return (
    <>
      {/* Mobile toggle */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between rounded-2xl bg-[#f8fafb] px-6 py-4 text-left font-bold lg:hidden"
      >
        <span>
          Filter{' '}
          {hasFilters && (
            <span className="text-sm font-normal text-gray-500">
              ({activeTypes.size + activeCategories.size} active)
            </span>
          )}
        </span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        >
          <path
            d="M5 7.5L10 12.5L15 7.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </button>

      {/* Sidebar content */}
      <div
        className={`w-full shrink-0 lg:block lg:w-[220px] ${
          isOpen ? 'block' : 'hidden'
        }`}
      >
        <div className="rounded-2xl bg-[#f8fafb] p-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <span className="text-xl font-bold text-black">Filter</span>
            {hasFilters && (
              <button
                type="button"
                onClick={onClearAll}
                className="text-sm font-semibold text-[#4b0fea] underline"
              >
                Clear all
              </button>
            )}
          </div>

          {/* Spacer */}
          <div className="mt-4" />

          {/* Type section */}
          <div className="border-b border-[#ccc] pb-6">
            <div className="flex flex-col gap-2">
              <p className="text-base font-semibold text-black">Type</p>
              <div className="flex flex-col gap-4">
                {CONTENT_TYPES.map((ct) => (
                  <label
                    key={ct.id}
                    className="flex cursor-pointer items-center gap-2"
                  >
                    <CheckBox
                      checked={activeTypes.has(ct.id)}
                      onChange={() => onToggleType(ct.id)}
                    />
                    <span className="text-sm font-bold text-black">
                      {ct.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            {activeTypes.size > 0 && (
              <button
                type="button"
                onClick={onClearTypes}
                className="mt-4 text-sm font-semibold text-[#4b0fea] underline"
              >
                Clear type
              </button>
            )}
          </div>

          {/* Topic section */}
          <div className="mt-4 flex flex-col gap-2">
            <p className="text-base font-semibold text-black">Topic</p>
            <div className="flex max-h-[400px] flex-col gap-4 overflow-y-auto">
              {categories.map((cat) => (
                <label
                  key={cat.id}
                  className="flex cursor-pointer items-center gap-2"
                >
                  <CheckBox
                    checked={activeCategories.has(cat.uid!)}
                    onChange={() => onToggleCategory(cat.uid!)}
                  />
                  <span className="text-sm font-bold text-black">
                    {cat.data?.name || cat.uid}
                  </span>
                </label>
              ))}
            </div>
            {activeCategories.size > 0 && (
              <button
                type="button"
                onClick={onClearCategories}
                className="mt-4 text-sm font-semibold text-[#4b0fea] underline"
              >
                Clear topic
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

interface BlogFilterGridProps {
  categories: BlogCategoryDocument[];
  initialCategory?: string;
  /** First page rendered on the server (default filters) for SEO. */
  initialPosts?: PostData[];
  initialHasMore?: boolean;
}

export default function BlogFilterGrid({
  categories,
  initialCategory,
  initialPosts = [],
  initialHasMore = false,
}: BlogFilterGridProps) {
  // Filter state
  const [activeTypes, setActiveTypes] = useState<Set<string>>(new Set());
  const [activeCategories, setActiveCategories] = useState<Set<string>>(
    () => new Set(initialCategory ? [initialCategory] : []),
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('newest');

  // Data state — seed from the server-rendered first page so the cards are in
  // the initial HTML (crawlable) with no skeleton flash on load.
  const [posts, setPosts] = useState<PostData[]>(initialPosts);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(
    initialPosts.length === 0,
  );

  const abortControllerRef = useRef<AbortController | null>(null);
  // True until the first client-side fetch runs. When the server seeded the
  // first page, skip that initial fetch (filters are still at defaults) while
  // allowing subsequent filter/search/sort changes to fetch normally.
  const skipInitialFetchRef = useRef(initialPosts.length > 0);

  // Listen for type filter events from ResourcesCenter tags
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { type: string | null };
      if (detail.type === null) {
        // "View All" — clear type filter
        setActiveTypes(new Set());
      } else {
        // Set exactly this type
        setActiveTypes(new Set([detail.type]));
      }
    };
    window.addEventListener('resource-type-filter', handler);
    return () => window.removeEventListener('resource-type-filter', handler);
  }, []);

  // Debounce search input (only apply if meets min length or is empty)
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchQuery.trim();
      setDebouncedSearch(
        trimmed.length === 0 || trimmed.length >= MIN_SEARCH_LENGTH
          ? trimmed
          : '',
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const isSearchActive = debouncedSearch.length > 0;

  // Force sort to "newest" when a search query is active (derived, not stored)
  const effectiveSortBy: SortOption = isSearchActive ? 'newest' : sortBy;

  // Build query string
  const buildQueryString = useCallback(
    (pageNum: number) => {
      const params = new URLSearchParams();

      if (activeTypes.size > 0) {
        params.set('types', Array.from(activeTypes).join(','));
      }
      if (activeCategories.size > 0) {
        params.set('categories', Array.from(activeCategories).join(','));
      }
      if (debouncedSearch) {
        params.set('search', debouncedSearch);
      }
      params.set('sort', effectiveSortBy);
      params.set('page', String(pageNum));
      params.set('pageSize', String(PAGE_SIZE));

      return params.toString();
    },
    [activeTypes, activeCategories, debouncedSearch, effectiveSortBy],
  );

  // Fetch posts
  const fetchPosts = useCallback(
    (pageNum: number, append = false) => {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      if (!append) {
        // Defer to a microtask so the effect that triggers a refetch does not
        // call setState synchronously within its body (cascading-render guard).
        queueMicrotask(() => setLoading(true));
      }

      const qs = buildQueryString(pageNum);
      fetch(`/api/blog/filter?${qs}`, { signal: controller.signal })
        .then((res) => {
          if (!res.ok) {
            throw new Error('Failed to fetch');
          }
          return res.json();
        })
        .then((data: ApiResponse) => {
          setPosts((prev) => (append ? [...prev, ...data.posts] : data.posts));
          setHasMore(data.hasMore);
          setPage(pageNum);
        })
        .catch((err: unknown) => {
          if (err instanceof Error && err.name === 'AbortError') {
            return;
          }
          console.error('Error fetching posts:', err);
        })
        .finally(() => {
          setLoading(false);
          setInitialLoading(false);
        });
    },
    [buildQueryString],
  );

  // Re-fetch when filters change
  useEffect(() => {
    // Skip the very first fetch when the server already seeded the default
    // page; re-run normally whenever filters/search/sort change.
    if (skipInitialFetchRef.current) {
      skipInitialFetchRef.current = false;
      return;
    }
    fetchPosts(1, false);
  }, [fetchPosts]);

  // Handlers
  const toggleType = useCallback((type: string) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const toggleCategory = useCallback((uid: string) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) {
        next.delete(uid);
      } else {
        next.add(uid);
      }
      return next;
    });
  }, []);

  const clearTypes = useCallback(() => setActiveTypes(new Set()), []);
  const clearCategories = useCallback(() => setActiveCategories(new Set()), []);
  const clearAll = useCallback(() => {
    setActiveTypes(new Set());
    setActiveCategories(new Set());
    setSearchQuery('');
    setDebouncedSearch('');
    setSortBy('relevant');
  }, []);

  const loadMore = useCallback(() => {
    if (!hasMore || loading) {
      return;
    }
    setLoading(true);
    fetchPosts(page + 1, true);
  }, [hasMore, loading, page, fetchPosts]);

  return (
    <section className="relative bg-[#eff1f3] px-5 pb-20 pt-16 md:px-10 lg:px-16 xl:px-[120px] 2xl:px-[200px]">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-6 lg:flex-row lg:gap-6">
        {/* Sidebar */}
        <FilterSidebar
          categories={categories}
          activeTypes={activeTypes}
          activeCategories={activeCategories}
          onToggleType={toggleType}
          onToggleCategory={toggleCategory}
          onClearTypes={clearTypes}
          onClearCategories={clearCategories}
          onClearAll={clearAll}
        />

        {/* Main content */}
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          {/* Search bar */}
          <div className="flex items-center justify-between rounded-2xl border-2 border-white/70 bg-[#f8fafb] px-6 py-3 backdrop-blur-[21px]">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search"
              className="flex-1 bg-transparent text-lg font-bold text-black placeholder:text-black/80 focus:outline-none"
              aria-label="Search blog posts"
              maxLength={100}
            />
            <SearchIcon />
          </div>

          {/* Sort options */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 sm:justify-end">
            {SORT_OPTIONS.map((opt) => {
              const disabled = isSearchActive && opt.value !== 'newest';

              return (
                <label
                  key={opt.value}
                  className={`flex items-center gap-2 ${
                    disabled
                      ? 'cursor-not-allowed opacity-40'
                      : 'cursor-pointer'
                  }`}
                  title={
                    disabled
                      ? 'For best match results we order by newest articles first'
                      : undefined
                  }
                >
                  <RadioButton
                    checked={effectiveSortBy === opt.value}
                    onChange={() => {
                      if (!disabled) {
                        setSortBy(opt.value);
                      }
                    }}
                  />
                  <span className="text-sm font-bold text-black">
                    {opt.label}
                  </span>
                </label>
              );
            })}
          </div>

          {/* Search hint */}
          {searchQuery.trim().length > 0 &&
            searchQuery.trim().length < MIN_SEARCH_LENGTH && (
              <p className="text-sm text-gray-500">
                Please enter at least {MIN_SEARCH_LENGTH} characters to search
              </p>
            )}

          {/* Card grid */}
          {initialLoading ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[455px] animate-pulse rounded-2xl bg-gray-200"
                />
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <p className="text-lg font-semibold text-gray-600">
                No results found
              </p>
              <p className="mt-2 text-sm text-gray-400">
                Try adjusting your filters or search terms
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => (
                <ResourceCard key={post.id} post={post} />
              ))}
            </div>
          )}

          {/* Loading indicator for load more */}
          {loading && !initialLoading && (
            <div className="flex justify-center py-6">
              <div className="size-8 animate-spin rounded-full border-2 border-[#00f557] border-t-transparent" />
            </div>
          )}

          {/* Load more button */}
          {hasMore && !loading && (
            <div className="flex justify-center pt-6">
              <button
                type="button"
                onClick={loadMore}
                className="rounded-full border border-[#00f557] px-6 py-3 text-base font-bold text-[#002726] transition-colors hover:bg-[#00f557]/10"
              >
                Load more
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
